import { downloadFileData } from '@/core/files/handle-files';
import { FileBaseInfo, FileData } from '@/core/files/types';

import { GrasshopperComputeResponse } from '../types';

import {
	extractFileData,
	getValue,
	getValues,
	GetValuesOptions,
	GetValuesResult,
	ParsedContext
} from '../io/output/response-processors';

/**
 * High-level wrapper for interacting with Grasshopper Compute responses.
 *
 * This class exposes a clean, consistent API for accessing parsed values,
 * geometry, and produced files. It is designed to be the primary interface
 * when working with Grasshopper results in client applications.
 */
export default class GrasshopperResponseProcessor {
	constructor(
		/**
		 * The raw compute response. Public so callers can hand it to a renderer-side parser — e.g.
		 * `getThreeMeshesFromComputeResponse` in `@selvajs/visualization/parse`, which replaced this
		 * class's former `extractMeshesFromResponse()`.
		 */
		public readonly response: GrasshopperComputeResponse,
		/**
		 * Retained for call-signature compatibility. Its only consumer was the removed
		 * `extractMeshesFromResponse()`, which merged it into the parse options; pass `debug`
		 * directly to the parser in `@selvajs/visualization/parse` instead.
		 */
		public readonly debug: boolean = false
	) {}

	/**
	 * Extract all values in the response.
	 *
	 * @typeParam T - Expected structure of the return value. Defaults to a simple key/value map. (later cast as needed)
	 * @param byId - Key by parameter ID instead of name. Requires the VektorNode rhino.compute branch.
	 * @param options - Controls parsing behavior such as Rhino geometry decoding.
	 * @returns Parsed Grasshopper output values.
	 *
	 * @example
	 * ```ts
	 * const processor = new GrasshopperResponseProcessor(response);
	 * const { values } = processor.getValues();
	 * ```
	 *
	 */
	public getValues<T = ParsedContext>(
		byId: boolean = false,
		options: GetValuesOptions = {}
	): GetValuesResult<T> {
		return getValues<T>(this.response, byId, options);
	}

	/**
	 * Retrieve a specific value by parameter name or ID.
	 *
	 * @param selector - `{ byName }` for the human-readable name, `{ byId }` for the parameter GUID.
	 * @param options - Parsing configuration (e.g. disable parsing or enable Rhino).
	 * @returns Single parsed value, array of values, or undefined if the parameter is absent.
	 *
	 * `byId` requires the VektorNode rhino.compute branch.
	 *
	 * @example
	 * ```ts
	 * const schema = processor.getValue({ byName: 'Schema' });
	 * const output = processor.getValue({ byId: 'a4be1c1e-23f9-4c27-b942-7f3bb2c45c6f' });
	 * ```
	 */
	public getValue(
		selector: { byName: string } | { byId: string },
		options?: GetValuesOptions
	): any {
		return getValue(this.response, selector, options);
	}

	/**
	 * REMOVED: `extractMeshesFromResponse()`. Mesh decoding lives in `@selvajs/visualization` now, so
	 * this package stays pure solve/data and carries no `three` dependency. Call the parser directly
	 * with the raw response:
	 *
	 * ```ts
	 * import { getThreeMeshesFromComputeResponse } from '@selvajs/visualization/parse';
	 *
	 * const meshes = await getThreeMeshesFromComputeResponse(processor.response, { rhino });
	 * scene.add(...meshes);
	 * ```
	 */

	private getFileData(): FileData[] {
		return extractFileData(this.response);
	}

	/**
	 * Download all files generated by Grasshopper, optionally including
	 * additional user-provided files.
	 *
	 * Files are grouped under the specified folder name when downloaded.
	 *
	 * @param folderName - Name for the download directory.
	 * @param additionalFiles - Extra files to package (single file, array, or null).
	 *
	 * @example
	 * ```ts
	 * await processor.getAndDownloadFiles('gh-output');
	 * ```
	 *
	 * @example
	 * ```ts
	 * const extra = { name: 'notes.txt', data: 'Example' };
	 * await processor.getAndDownloadFiles('project', extra);
	 * ```
	 */
	public async getAndDownloadFiles(
		folderName: string,
		additionalFiles?: FileBaseInfo[] | FileBaseInfo | null
	): Promise<void> {
		const files = this.getFileData();
		await downloadFileData(files, folderName, additionalFiles);
	}
}
